import { Checkbox, Alert, FormControl, FormControlLabel, FormHelperText, FormLabel, Grid2, LinearProgress, Link, List, ListItem, ListItemText, MenuItem, Paper, Radio, RadioGroup, Slider, TextField, ToggleButton, Tooltip, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import enIE from "date-fns/esm/locale/en-IE/index";
import { useEffect, useRef, useState } from "react";
import { SelectValidator, ValidatorForm } from "react-material-ui-form-validator";
import { useNavigate, useParams } from "react-router-dom";
import { FormFieldKeyConditionEnum } from "../../../_common/enum/forms/fomFieldKeyCondition.enum";
import { FormFieldKeyConditionTypeEnum } from "../../../_common/enum/forms/fomFieldKeyConditionType.enum";
import { FormFieldKeyActionEnum } from "../../../_common/enum/forms/formFieldKeyAction.enum";
import { FormFieldValueTypeEnum } from "../../../_common/enum/forms/formFieldValueType.enum";
import { FormStatusEnum } from "../../../_common/enum/forms/formStatus.enum";
import { IFormFieldManage } from "../../../_common/models/formField";
import { IFormFieldKeyManage } from "../../../_common/models/formFieldKey";
import { IFormFieldKeyLookupManage } from "../../../_common/models/formFieldKeyLookup";
import { IFormTemplateSectionFormManage } from "../../../_common/models/formTemplateSection/formTemplateSectionFormManage.model";
import { DataTypeAutoComplete } from "../../../_components/autocomplete/dataType.autocomplete";
import { StandardButton } from "../../../_components/button/primary.button";
import BookingDatePicker from "../../../_components/datepicker/bookingDatepicker";
import { StandardTextValidator } from "../../../_components/textValidator/standardTextValidator";
import { API_URL } from "../../../_config/config.const";
import { TERTIARY_COLOR } from "../../../_const/color.const";
import { handleErrorResponse, handleErrorResponseAxios } from "../../../_helpers/handleResponse.helper";
import { accountService } from "../../../_services/accounts.service";
import { formService } from "../../../_services/form.service";
import { formSectionService } from "../../../_services/formSection.service";
import { formTemplateSectionService } from "../../../_services/formTemplateSection.service";
import { tokenService } from "../../../_services/token.service";
import { ParentItem } from "../../../_services/dropdown.service";
import { DataTypeMultipleAutoComplete } from "../../../_components/autocomplete/dataTypeMultiple.autocomplete";
import { toastr } from "../../../_components/toastr/toastr";
import { formatIrishPhone, isPhoneField } from "../../../_helpers/phoneNumber.helper";
import { calendarService } from "../../../_services/calendar.service";
import {
    FormFieldSpecialType,
    allowsSplitSlots,
    getExtraDisabledDatesForVisit,
    getSiblingOccupancy,
    getSubsectionFieldValue,
    isPeriodOptionDisabled,
    isSlotSessionType,
    isVisitDateSelectionReady,
    parseBookedDatesResponse,
    type DateOccupancy
} from "../../../_helpers/sessionPeriod.helper";
import {
    CORRECT_ANSWER_VALIDATION_MESSAGE,
    getCorrectAnswerLookupValues,
    getCorrectAnswerValidationRuleName,
    getDuplicateVisitDatesFromFormFieldKeys,
    getIncorrectCorrectAnswerFields,
    hasCorrectAnswerRule,
    isCorrectAnswerValueSelected,
    registerCorrectAnswerValidationRule,
    removeCorrectAnswerValidationRule
} from "../../../_helpers/formValidation.helper";


export function FormSectionManage(params: {
    formTemplateSectionId: number,
    formSectionId?: number,
    isSectionSaving?: boolean,
    isForReview?: boolean,
    actionId?: FormFieldKeyActionEnum,
    onSave: () => void,
}) {
    const signatureRef = useRef(null);

    const [data, setData] = useState<IFormTemplateSectionFormManage>();
    const [isLoading, setLoading] = useState(false);
    const [saving, setIsSaving] = useState(false);
    const [isContinue, setContinue] = useState(false);
    const [occupancy, setOccupancy] = useState<DateOccupancy[]>([]);

    const { formId } = useParams<{ formId: string }>();
    const _formId = params.formSectionId ?? 0;

    const navigate = useNavigate();

    useEffect(() => {
        fetchTemplateSectionData();
    }, [params.formTemplateSectionId, _formId, params.isForReview, params.actionId]);

    useEffect(() => {
        const ruleNames: string[] = [];

        data?.formFieldKeys?.forEach(ffk => {
            const correctAnswers = getCorrectAnswerLookupValues(ffk.formFieldKeyLookups);
            if (!correctAnswers.length) {
                return;
            }

            const ruleName = getCorrectAnswerValidationRuleName(ffk.id);
            registerCorrectAnswerValidationRule(ruleName, correctAnswers);
            ruleNames.push(ruleName);
        });

        return () => {
            ruleNames.forEach(removeCorrectAnswerValidationRule);
        };
    }, [data]);

    const specialistValue = data?.formFieldKeys?.find(m => m.shortName === 'specialist')?.formField?.fieldValue;
    const schoolTypeValue = data?.globalFormFields?.find(m => m.shortName === 'schooltype')?.fieldValue;

    useEffect(() => {
        if (!specialistValue) {
            setOccupancy([]);
            return;
        }
        calendarService
            .getBookedDates(specialistValue)
            .then((json) => setOccupancy(parseBookedDatesResponse(json).occupancy), handleErrorResponse);
    }, [specialistValue]);

    function fetchTemplateSectionData() {
        if (params.formTemplateSectionId == 0) return;
        setLoading(true);

        return formTemplateSectionService
            .getForm(params.formTemplateSectionId, _formId, params.isForReview, params.actionId)
            .then(
                (json) => {
                    setData(json);
                }
            )
            .finally(() => {
                setLoading(false);
            });
    }

    function handleChange(event: any, ffkId: number) {

        setData((_data: any) => {
            var __data = { ..._data };

            if (__data.formFieldKeys && __data.formFieldKeys.length) {
                var _formFieldKey = __data.formFieldKeys.find((ffk: IFormFieldKeyManage) => ffk.id === ffkId);
                if (_formFieldKey) {
                    var isCheckbox = _formFieldKey.formFieldValueTypeId === FormFieldValueTypeEnum.Checkbox;
                    var nextValue = isCheckbox ? event.target.checked ? "Yes" : "No" : event.target.value;
                    if (!isCheckbox && isPhoneField(_formFieldKey) && typeof nextValue === "string")
                        nextValue = formatIrishPhone(nextValue);

                    if (_formFieldKey.formField) {
                        _formFieldKey.formField.fieldValue = nextValue;
                    }
                    else {
                        _formFieldKey.formField = {
                            id: 0,
                            fieldValue: nextValue,
                            formFieldKeyId: _formFieldKey.id,
                            formTemplateSectionId: _formFieldKey.formTemplateSectionId,
                            formSectionId: params.formSectionId ?? 0
                        };
                    }
                }

                var _dependantKeys = __data.formFieldKeys.filter((ffk: IFormFieldKeyManage) => ffk.formFieldKeyValuePopulatorId === ffkId);
                if (!!_dependantKeys && !!_dependantKeys.length && !!event.target.value) {
                    _dependantKeys.forEach((_dependantFormFieldKey: IFormFieldKeyManage) => {
                        var populatedValue = _dependantFormFieldKey.formFieldKeyValuePopulatorPropertyName ? event.target[_dependantFormFieldKey.formFieldKeyValuePopulatorPropertyName] : event.target.value;
                        if (isPhoneField(_dependantFormFieldKey) && typeof populatedValue === "string")
                            populatedValue = formatIrishPhone(populatedValue);
                        if (_dependantFormFieldKey.formField) {
                            _dependantFormFieldKey.formField.fieldValue = populatedValue;
                        }
                        else {
                            _dependantFormFieldKey.formField = {
                                id: 0,
                                fieldValue: populatedValue,
                                formFieldKeyId: _dependantFormFieldKey.id,
                                formTemplateSectionId: _dependantFormFieldKey.formTemplateSectionId,
                                formSectionId: params.formSectionId ?? 0
                            } as IFormFieldManage;
                        }
                    });
                }

                if (_formFieldKey
                    && (_formFieldKey.formFieldKeySpecialTypeId === FormFieldSpecialType.SessionTypeName
                        || _formFieldKey.formFieldKeySpecialTypeId === FormFieldSpecialType.SessionPeriod)) {
                    const schoolType = __data.globalFormFields?.find((m: any) => m.shortName === 'schooltype')?.fieldValue;
                    const sessionType = getSubsectionFieldValue(__data.formFieldKeys, _formFieldKey.subSectionId, FormFieldSpecialType.SessionTypeName);
                    const sessionPeriod = getSubsectionFieldValue(__data.formFieldKeys, _formFieldKey.subSectionId, FormFieldSpecialType.SessionPeriod);

                    if (_formFieldKey.formFieldKeySpecialTypeId === FormFieldSpecialType.SessionTypeName
                        && !(allowsSplitSlots(schoolType) && isSlotSessionType(sessionType))) {
                        const periodField = __data.formFieldKeys.find((ffk: IFormFieldKeyManage) =>
                            ffk.subSectionId == _formFieldKey.subSectionId
                            && ffk.formFieldKeySpecialTypeId === FormFieldSpecialType.SessionPeriod);
                        if (periodField?.formField) {
                            periodField.formField.fieldValue = "";
                        }
                    }

                    if (!isVisitDateSelectionReady(sessionType, sessionPeriod, schoolType)) {
                        const dateField = __data.formFieldKeys.find((ffk: IFormFieldKeyManage) =>
                            ffk.subSectionId == _formFieldKey.subSectionId
                            && ffk.formFieldKeySpecialTypeId === FormFieldSpecialType.SessionDateTime);
                        if (dateField?.formField) {
                            dateField.formField.fieldValue = "";
                        }
                    }
                }


            }
            return { ..._data } as IFormTemplateSectionFormManage;
        });
    };

    function submit(event: any) {
        setContinue(false)
        event.preventDefault();
        var _formFields: Array<any> = [];
        if (data && data.formFieldKeys && data.formFieldKeys.length) {
            _formFields = data.formFieldKeys.map(ffk => {
                var _formField = ffk.formField || {
                    id: 0,
                    fieldValue: "",
                    fieldValueTest: "",
                    formFieldKeyId: ffk.id,
                    formTemplateSectionId: ffk.formTemplateSectionId,
                    formSectionId: params.formSectionId ?? 0
                }

                if (ffk.formFieldValueTypeId === FormFieldValueTypeEnum.Signature) {
                    _formField.fieldValue = (signatureRef.current as any).getTrimmedCanvas().toDataURL("image/png");
                }

                if (isPhoneField(ffk) && typeof _formField.fieldValue === "string")
                    _formField.fieldValue = formatIrishPhone(_formField.fieldValue);

                return _formField;
            });
        }

        //Need to check for duplicate dates here
        const duplicateDatesArr = getDuplicateVisitDatesFromFormFieldKeys(data?.formFieldKeys ?? [], schoolTypeValue);

        if (duplicateDatesArr.length > 0) {
            toastr.error(`Incoming visits contain duplicate dates: ${duplicateDatesArr.join(", ")}`);
            return;
        }

        const incorrectCorrectAnswerFields = getIncorrectCorrectAnswerFields(data?.formFieldKeys ?? []);
        if (incorrectCorrectAnswerFields.length > 0) {
            toastr.error(CORRECT_ANSWER_VALIDATION_MESSAGE);
            return;
        }

        var form = {
            id: params.formSectionId ?? 0,
            formId: _formId ?? 0,
            formTemplateSectionId: params.formTemplateSectionId ?? 0,
            formSectionId: params.formSectionId ?? 0,
            formFields: _formFields
        };

        var formData = new FormData();
        formData.append(`id`, form.id.toString());
        formData.append(`formId`, form.formId.toString());
        formData.append(`formTemplateSectionId`, form.formTemplateSectionId.toString());
        formData.append(`formSectionId`, form.formSectionId.toString());

        form.formFields.forEach((formField, index) => {
            for (var formFieldName in formField) {
                var val = (formField as any)[formFieldName];
                formData.append(`formFields[${index}].${formFieldName}`, val || (val === null || val === "" || val === " " ? "" : 0));
            }
        });

        var documentUploadParentFields = document.getElementsByClassName("documentUpload");
        var documentsToClear: Array<string> = []

        if (documentUploadParentFields && documentUploadParentFields.length > 0) {
            var currentIndex = 0;

            Array.from(documentUploadParentFields).forEach((documentUploadParentField: any, index: number) => {

                var fileField = documentUploadParentField.getElementsByClassName("MuiInputBase-input")[0];
                documentsToClear.push(fileField.id);

                var id = fileField.id.split("_")[1];

                if (data && data.formFieldKeys && data.formFieldKeys.length && (fileField as any)?.files[0]) {
                    formData.append("file[" + currentIndex + "].file", (fileField as any)?.files[0]);
                    formData.append("file[" + currentIndex + "].formFieldKeyId", id);
                    currentIndex++
                }
            })
        }

        setIsSaving(true);
        formSectionService[params.actionId ? "updateOnFormActionSave" : "postFromData"](formData).then(
            (data) => {

                documentsToClear.forEach(d => {
                    var doc = document.getElementById(d);
                    (doc as any).value = null;
                });

                params.onSave();
            }, handleErrorResponseAxios)
            .finally(() => {
                setIsSaving(false)
            });
    }


    function valuetext(value: number) {
        return `€${value.toLocaleString()}`;
    }

    function getSliderMarks(minValue?: number, maxValue?: number) {
        minValue = minValue || 0;
        maxValue = maxValue || 9999;
        var marks = [];

        var val = Math.ceil(maxValue / 4);
        for (var i = 0; i < 5; i++) {

            marks.push({
                value: val * i,
                label: valuetext(val * i),
            })
        }

        return marks;
    }

    function getOptionFieldValidators(field: IFormFieldKeyManage) {
        const validators: string[] = [];
        const errorMessages: string[] = [];

        if (field.isRequired) {
            validators.push("required");
            errorMessages.push("This field is required");
        }

        if (hasCorrectAnswerRule(field.formFieldKeyLookups)) {
            validators.push(getCorrectAnswerValidationRuleName(field.id));
            errorMessages.push(CORRECT_ANSWER_VALIDATION_MESSAGE);
        }

        return { validators, errorMessages };
    }

    function getOptionFieldValidationError(field: IFormFieldKeyManage) {
        if (!isContinue) {
            return null;
        }

        const value = field.formField?.fieldValue;
        if (field.isRequired && !value) {
            return "This field is required";
        }

        if (value && hasCorrectAnswerRule(field.formFieldKeyLookups) && !isCorrectAnswerValueSelected(value, field.formFieldKeyLookups)) {
            return CORRECT_ANSWER_VALIDATION_MESSAGE;
        }

        return null;
    }

    function getField(field: IFormFieldKeyManage, index: number) {
        var validators = [];
        var validatorMessages = [];
        var isDisabled = params.isForReview;
        var variant = "outlined";

        if (field.isRequired) {
            validators.push("required");
            validatorMessages.push("This field is required");
        }

        switch (field.formFieldValueTypeId) {
            case (FormFieldValueTypeEnum.String):
                return <StandardTextValidator
                    label={field.label}
                    value={field.formField?.fieldValue ?? ""}
                    name={field.shortName}
                    required={field.isRequired}
                    helperText={field.description}
                    fullWidth
                    disabled={isDisabled}
                    type={"text"}
                    variant={variant}
                    validators={[...validators]}
                    errorMessages={[...validatorMessages]}
                    onChange={(e: any) => { handleChange(e, index) }}
                />; break;
            case (FormFieldValueTypeEnum.Date):
                return <StandardTextValidator
                    label={field.label}
                    value={field.formField?.fieldValue ?? ""}
                    name={field.shortName}
                    required={field.isRequired}
                    fullWidth
                    helperText={field.description}
                    type={"date"}
                    disabled={isDisabled}
                    variant={variant}
                    validators={[...validators]}
                    errorMessages={[...validatorMessages]}
                    onChange={(e: any) => { handleChange(e, index) }}
                />;
                break;
            case (FormFieldValueTypeEnum.Email):
                return <StandardTextValidator
                    label={field.label}
                    value={field.formField?.fieldValue ?? ""}
                    name={field.shortName}
                    required={field.isRequired}
                    fullWidth
                    helperText={field.description}
                    type={"email"}
                    disabled={isDisabled}
                    variant={variant}
                    validators={[...validators, "isEmail"]}
                    errorMessages={[...validatorMessages, 'Email is not valid']}
                    onChange={(e: any) => { handleChange(e, index) }}
                />;
                break;
            case (FormFieldValueTypeEnum.PhoneNumber):
                return <StandardTextValidator
                    label={field.label}
                    value={field.formField?.fieldValue ?? ""}
                    name={field.shortName}
                    required={field.isRequired}
                    variant={variant}
                    fullWidth
                    helperText={field.description}
                    type={"text"}
                    disabled={isDisabled}
                    validators={field.isRequired ? ['required'] : []}
                    errorMessages={field.isRequired ? ['This field is required'] : []}
                    onChange={(e: any) => { handleChange(e, index) }}
                />;
                break
            case (FormFieldValueTypeEnum.Dropdown):
                return <SelectValidator
                    value={field.formField?.fieldValue ?? ""}
                    onChange={(e: any) => { handleChange(e, index) }}
                    required={field.isRequired}
                    label={field.label}
                    name="formFieldValueTypeId"
                    helperText={field.description}
                    variant={variant}
                    fullWidth
                    disabled={isDisabled}
                    validators={getOptionFieldValidators(field).validators}
                    errorMessages={getOptionFieldValidators(field).errorMessages}
                    InputLabelProps={{
                        shrink: !!field.formField?.fieldValue,
                    }}>

                    {field.formFieldKeyLookups?.filter(m => checkIfLookupDependancyMet(m)).map(lookup =>
                        <MenuItem key={"list_item_menu_" + field.id + "_" + lookup.id} value={lookup.lookupValue}>{lookup.label || lookup.lookupValue}</MenuItem>
                    )}
                </SelectValidator>
                break
            case (FormFieldValueTypeEnum.Radio):
                return <FormControl>
                    <FormLabel id={field.label}>{`${field.label} ${field.isRequired ? "*" : ""}`}</FormLabel>
                    <RadioGroup
                        row
                        aria-labelledby={field.label}
                        onChange={(e: any) => { handleChange(e, index) }}
                        name={`${field.shortName}`}>

                        {field.formFieldKeyLookups?.filter(m => checkIfLookupDependancyMet(m)).map(lookup =>
                            <>
                                <FormControlLabel
                                    key={"list_item_radio_" + field.id + "_" + lookup.id}
                                    required={field.isRequired}
                                    checked={(!!field.formField?.fieldValue && lookup.lookupValue.toLowerCase() == field.formField?.fieldValue.toLowerCase()) ? true : false}
                                    value={lookup.lookupValue}
                                    label={lookup.label}
                                    control={
                                        <Tooltip title={lookup.description}>
                                            <Radio
                                                disabled={isDisabled || (field.formFieldKeySpecialTypeId === FormFieldSpecialType.SessionPeriod && isPeriodOptionDisabled(
                                                    lookup.lookupValue,
                                                    getSubsectionFieldValue(data?.formFieldKeys ?? [], field.subSectionId, FormFieldSpecialType.SessionDateTime),
                                                    occupancy,
                                                    getSiblingOccupancy(
                                                        data?.formFieldKeys ?? [],
                                                        data?.formFieldKeys?.find(m => m.subSectionId == field.subSectionId && m.formFieldKeySpecialTypeId === FormFieldSpecialType.SessionDateTime)?.id ?? field.id,
                                                        schoolTypeValue
                                                    )
                                                ))}
                                                key={"list_item_radio_sub_" + field.id + "_" + lookup.id}
                                            />
                                        </Tooltip>
                                    }
                                />
                            </>
                        )}
                        {getOptionFieldValidationError(field) && <Grid2 container> <Grid2 size={{ xs: 12 }}><FormHelperText style={{ "color": "#d32f2f" }}>{getOptionFieldValidationError(field)}</FormHelperText></Grid2></Grid2>}
                    </RadioGroup>

                    <StandardTextValidator
                        label={field.label}
                        value={field.formField?.fieldValue ?? ""}
                        name={field.shortName}
                        required={field.isRequired}
                        hidden
                        type={"text"}
                        disabled={isDisabled}
                        variant={variant}
                        validators={getOptionFieldValidators(field).validators}
                        errorMessages={getOptionFieldValidators(field).errorMessages}
                        onChange={(e: any) => { handleChange(e, index) }}
                    />
                </FormControl>
                break;
            case (FormFieldValueTypeEnum.Text):
                return <Typography textAlign="left" variant={field.textFontSize ?? "h6"}>{field.label}</Typography>
                break;
            case (FormFieldValueTypeEnum.Textarea):
                return <StandardTextValidator
                    label={field.label}
                    value={field.formField?.fieldValue ?? ""}
                    name={field.shortName}
                    required={field.isRequired}
                    fullWidth
                    multiline
                    helperText={field.description}
                    type={"text"}
                    rows={4}
                    disabled={isDisabled}
                    variant={variant}
                    validators={field.isRequired ? ['required'] : []}
                    errorMessages={field.isRequired ? ['This field is required'] : []}
                    onChange={(e: any) => { handleChange(e, index) }}
                />;
            case (FormFieldValueTypeEnum.Number):
                return <StandardTextValidator
                    label={field.label}
                    value={field.formField?.fieldValue ?? ""}
                    name={field.shortName}
                    required={field.isRequired}
                    fullWidth
                    helperText={field.description}
                    type={"number"}
                    disabled={isDisabled}
                    variant={variant}
                    validators={field.isRequired ? ['required'] : []}
                    errorMessages={field.isRequired ? ['This field is required'] : []}
                    onChange={(e: any) => { handleChange(e, index) }}
                />;
            case (FormFieldValueTypeEnum.Signature):
                // return !params.isForReview ?
                // <div style={{ border: "1px solid silver" }}>Signature {field.isRequired ? " *" : ""}
                //     <SignatureCanvas penColor="black"
                //         ref={signatureRef}
                //         canvasProps={{ width: 800, height: 200, className: 'sigCanvas' }} />
                //     <Grid2 container spacing={1}>
                //         <Grid2 size={{ xs: 8 }} >
                //             <StandardButton icon="check" type="button" onClick={() => { handleChange({ target: { value: (signatureRef.current as any)?.getTrimmedCanvas()?.toDataURL("image/png") } }, index) }}>Update Signature</StandardButton>
                //         </Grid2>
                //         <Grid2 size={{ xs: 4 }}>
                //             <StandardButton icon="remove" type="button" onClick={() => { handleChange({ target: { value: (signatureRef.current as any)?.clear() } }, index) }}>Clear Signature</StandardButton>
                //         </Grid2>
                //     </Grid2 >
                // </div > :
                // <img src={field.formField?.fieldValue} style={{ "maxWidth": "100%" }} width="800px" height="200px" />
                break;
            case (FormFieldValueTypeEnum.FileUpload):
                return <Grid2 size={{ xs: 12 }}>
                    <Grid2 container>
                        <Grid2 size={{ xs: 6 }} >
                            {(!params.isForReview) && <TextField
                                className={"documentUpload"}
                                id={"documentUpload_" + field.id}
                                label={field.label}
                                defaultValue={""}
                                name={field.shortName}
                                required={field.isRequired && !field.formField?.id}
                                type={"file"}
                                disabled={isDisabled}
                                error={field.isRequired && !field.formField?.id && !field.formField?.fieldValue}
                                helperText={field.isRequired && !field.formField?.id && !field.formField?.fieldValue ? 'This field is required' : ""}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                onChange={(e: any) => { handleChange(e, index) }}
                            />}
                        </Grid2>
                        <Grid2 size={{ xs: 6 }}>
                            {(!field.formField?.id) && (
                                <StandardTextValidator
                                    label={field.label}
                                    value={field.formField?.fieldValue ?? ""}
                                    name={field.shortName}
                                    required={field.isRequired}
                                    hidden
                                    type={"text"}
                                    disabled={isDisabled}
                                    variant={variant}
                                    validators={field.isRequired ? ['required'] : []}
                                    errorMessages={field.isRequired ? ['This field is required'] : []}
                                    onChange={(e: any) => { handleChange(e, index) }}
                                />
                            )}

                            {((field.formField?.id && field.formField?.fieldValue)) && (
                                <StandardButton icon={"download"} variant="primary" type="button" onClick={() => {
                                    tokenService.GetReportToken().then((data) => {
                                        return window.open(API_URL + "/formField/GetUploadById/?token=" + data + "&fileId=" + field.formField?.id, "_blank");
                                    });
                                }}>{field.shortName || field.label}
                                </StandardButton>
                            )}

                            {params.isForReview && (!field.formField?.id || !field.formField?.fieldValue) && (
                                <Alert severity="warning">{field.label} Not provided</Alert>
                            )}
                        </Grid2>
                    </Grid2>
                </Grid2 >;
                break;
            case (FormFieldValueTypeEnum.ImageUpload):
                return (
                    <Grid2 size={{ xs: 12 }}>
                        <Grid2 container>
                            <Grid2 size={{ xs: 6 }}>
                                {!params.isForReview && (
                                    <TextField
                                        className={"documentUpload"}
                                        id={"documentUpload_" + field.id}
                                        label={field.label}
                                        defaultValue={""}
                                        name={field.shortName}
                                        required={field.isRequired && !field.formField?.id}
                                        type={"file"}
                                        disabled={isDisabled}
                                        error={field.isRequired && !field.formField?.id && !field.formField?.fieldValue}
                                        helperText={field.isRequired && !field.formField?.id && !field.formField?.fieldValue ? 'This field is required' : ""}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        onChange={(e: any) => { handleChange(e, index) }}
                                    />
                                )}
                            </Grid2>
                            <Grid2 size={{ xs: 6 }}>
                                {!field.formField?.id && (
                                    <StandardTextValidator
                                        label={field.label}
                                        value={field.formField?.fieldValue ?? ""}
                                        name={field.shortName}
                                        required={field.isRequired}
                                        hidden
                                        type={"text"}
                                        disabled={isDisabled}
                                        variant={variant}
                                        validators={field.isRequired ? ['required'] : []}
                                        errorMessages={field.isRequired ? ['This field is required'] : []}
                                        onChange={(e: any) => { handleChange(e, index) }}
                                    />
                                )}

                                {((field.formField?.id && field.formField?.fieldValue)) && (
                                    <StandardButton icon={"download"} variant="primary" type="button" onClick={() => {
                                        tokenService.GetReportToken().then((data) => {
                                            return window.open(API_URL + "/formField/GetUploadById/?token=" + data + "&fileId=" + field.formField?.id, "_blank");
                                        });
                                    }}>{field.shortName || field.label}</StandardButton>
                                )}

                                {params.isForReview && (!field.formField?.id || !field.formField?.fieldValue) && (
                                    <Alert severity="warning">{field.label} Not provided</Alert>
                                )}
                            </Grid2>
                        </Grid2>
                    </Grid2>
                );

            case (FormFieldValueTypeEnum.List):
                return <>

                    {field.showLabel && <Typography>
                        {field.label}
                    </Typography>
                    }

                    <List sx={{ listStyle: "decimal", pl: 4 }}>

                        {field.formFieldKeyLookups?.filter(m => checkIfLookupDependancyMet(m)).map(lookup =>
                            <ListItem key={"list_item_list_" + lookup.id} sx={{ display: "list-item" }}>
                                <ListItemText primary={lookup.label || lookup.lookupValue} />
                            </ListItem>
                        )}
                    </List></>
                break;
            case (FormFieldValueTypeEnum.Title):
                return <Typography variant="h5" component="h5" textAlign="center" color={isDisabled ? "textDisabled" : TERTIARY_COLOR}>{field.label}</Typography>
                break;
            case (FormFieldValueTypeEnum.Image):
                return <img style={{
                    width: "100%",
                    marginTop: "10px",
                    display: 'block',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    marginBottom: "10px"
                }} src={"data:image/jpeg;charset=utf-8;base64," + field.label} alt={"test"}></img>
                break;
            case (FormFieldValueTypeEnum.Checkbox):
                return <>
                    <FormControlLabel
                        disabled={isLoading || params.isForReview}
                        value={field.formField?.fieldValue}
                        required={field.isRequired}
                        style={{ "paddingLeft": "16px" }}
                        onChange={(e: any) => { handleChange(e, index) }}
                        checked={(field.formField?.fieldValue && field.formField?.fieldValue.toLowerCase() == "yes") ? true : false}
                        control={<Checkbox style={{ "paddingRight": "8px" }} value="Yes" color="primary" />}
                        label={field.label} />
                    {(field.isRequired && isContinue && !field.formField?.fieldValue) && <Grid2 container> <Grid2 size={{ xs: 12 }}><FormHelperText style={{ "color": "#d32f2f" }}>This field is required</FormHelperText></Grid2></Grid2>}
                    <StandardTextValidator
                        label={field.label}
                        value={field.formField?.fieldValue ?? ""}
                        name={field.shortName}
                        required={field.isRequired}
                        hidden
                        type={"text"}
                        disabled={isDisabled}
                        variant={variant}
                        validators={field.isRequired ? ['required'] : []}
                        errorMessages={field.isRequired ? ['This field is required'] : []}
                        onChange={(e: any) => { handleChange(e, index) }}
                    />
                </>
                break;

            case (FormFieldValueTypeEnum.FileDownload):
                return <StandardButton disabled={params.isForReview} icon={"download"} variant="primary" type="button" onClick={() => {
                    tokenService.GetReportToken().then((data) => {
                        return window.open(API_URL + "/formField/GetDownloadById/?fileId=" + field.label + "&token=" + data, "_blank");
                    });
                }}>{field.shortName || field.label}</StandardButton>
                break;

            case (FormFieldValueTypeEnum.RadioButton):
                return <FormControl fullWidth>
                    {field.showLabel && <FormLabel id={field.label}>{field.label} {field.isRequired ? "*" : ""}</FormLabel>}

                    <Grid2 container spacing={1}>
                        {field.formFieldKeyLookups?.filter(m => checkIfLookupDependancyMet(m)).map(lookup =>
                            <Grid2 size={{ xs: (field.formFieldKeyLookups?.filter(m => checkIfLookupDependancyMet(m)).length || 0) % 8 == 0 ? 3 : (field.formFieldKeyLookups?.length || 0) % 7 == 0 ? 3 : (field.formFieldKeyLookups?.length || 0) % 6 == 0 ? 4 : (field.formFieldKeyLookups?.length || 0) % 5 == 0 ? 4 : (field.formFieldKeyLookups?.length || 0) % 4 == 0 ? 3 : (field.formFieldKeyLookups?.length || 0) % 3 == 0 ? 4 : (field.formFieldKeyLookups?.length || 0) % 2 == 0 ? 6 : 12 }} >
                                <ToggleButton disabled={isDisabled} fullWidth color={isDisabled ? "standard" : "primary"} onChange={(e: any) => { handleChange(e, index) }} selected={!!field.formField?.fieldValue && field.formField?.fieldValue.toLowerCase() === lookup.lookupValue.toLowerCase()} value={lookup.lookupValue} aria-label="left aligned">
                                    {lookup.label || lookup.lookupValue}
                                </ToggleButton>
                            </Grid2>
                        )}
                        {getOptionFieldValidationError(field) && <Grid2 container> <Grid2 size={{ xs: 12 }}><FormHelperText style={{ "color": "#d32f2f" }}>{getOptionFieldValidationError(field)}</FormHelperText></Grid2></Grid2>}
                        <StandardTextValidator
                            label={field.label}
                            value={field.formField?.fieldValue ?? ""}
                            name={field.shortName}
                            required={field.isRequired}
                            hidden
                            type={"text"}
                            disabled={isDisabled}
                            variant={variant}
                            validators={getOptionFieldValidators(field).validators}
                            errorMessages={getOptionFieldValidators(field).errorMessages}
                            onChange={(e: any) => { handleChange(e, index) }}
                        />

                    </Grid2>

                </FormControl >
                break;

            case (FormFieldValueTypeEnum.Slider):
                return <>

                    <FormControl fullWidth>
                        <FormLabel id={field.label}>{field.label}: {valuetext(Number(field.formField?.fieldValue || 0)).toString()}</FormLabel>
                        <Grid2 container spacing={6}>
                            <Grid2 size={{ xs: 6 }} >
                                <Slider
                                    aria-label="Custom marks"
                                    defaultValue={field.minValue || 0}
                                    value={Number(field.formField?.fieldValue || "")}
                                    onChange={(e: any) => { handleChange(e, index) }}
                                    getAriaValueText={valuetext}
                                    step={1000}
                                    min={field.minValue}
                                    max={field.maxValue}
                                    valueLabelDisplay="off"
                                    name={field.shortName}
                                    marks={getSliderMarks(field.minValue, field.maxValue)}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 2 }} >
                                <StandardTextValidator
                                    value={field.formField?.fieldValue ?? "0"}
                                    name={field.shortName}
                                    required={field.isRequired}
                                    fullWidth
                                    type={"number"}
                                    disabled={isDisabled}
                                    variant={variant}
                                    validators={field.isRequired ? ['required'] : []}
                                    errorMessages={field.isRequired ? ['This field is required'] : []}
                                    onChange={(e: any) => { handleChange(e, index) }}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 2 }} >

                            </Grid2 >
                        </Grid2 >
                    </FormControl >
                </>
                break;
            case (FormFieldValueTypeEnum.WarningAlert):
                return <Alert severity="warning">{field.label}</Alert>
                break;
            case (FormFieldValueTypeEnum.InfoAlert):
                return <Alert severity="info">{field.label}</Alert>
                break;
            case (FormFieldValueTypeEnum.SuccessAlert):
                return <Alert severity="success">{field.label}</Alert>
                break;
            case (FormFieldValueTypeEnum.DangerAlert):
                return <Alert severity="error">{field.label}</Alert>
                break;
            case (FormFieldValueTypeEnum.VideoEmbed):
                return <iframe
                    width="560"
                    height="315"
                    src={"https://www.youtube.com/embed/" + field.label} title="YouTube video player" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" />
                break;
            case (FormFieldValueTypeEnum.DataTypeAutoComplete):
                return <>
                    <DataTypeMultipleAutoComplete
                        dataType={field.dataType}
                        label={field.label}
                        valueField={field.dataTypeValueFieldName ?? "name"}
                        autoSelectSingle={field.autoSelectSingle ?? false}
                        hasParent={!!field.formFieldKeyDependencyId || (!!field.formFieldKeyParentDependencies && !!field.formFieldKeyParentDependencies.length)}
                        parentId={getMultipleFromFieldValue(field.formFieldKeyParentDependencies)}
                        value={field.formField?.fieldValue ?? ""}
                        name={field.shortName}
                        required={field.isRequired}
                        disabled={isDisabled}
                        onChange={(e: any) => {
                            handleChange(e, index)
                        }}
                    />
                </>
                break;
            case (FormFieldValueTypeEnum.DataTypeMultipleAutoComplete):
                return <>
                    <DataTypeMultipleAutoComplete
                        dataType={field.dataType}
                        label={field.label}
                        valueField={field.dataTypeValueFieldName ?? "name"}
                        multiple
                        asCsv
                        autoSelectSingle={field.autoSelectSingle ?? false}
                        helperText={field.description}
                        hasParent={!!field.formFieldKeyDependencyId || (!!field.formFieldKeyParentDependencies && !!field.formFieldKeyParentDependencies.length)}
                        parentId={getMultipleFromFieldValue(field.formFieldKeyParentDependencies)}
                        value={field.formField?.fieldValue ?? ""}
                        name={field.shortName}
                        required={field.isRequired}
                        disabled={isDisabled}
                        onChange={(e: any, item) => {
                            handleChange(e, index)
                        }}
                    />
                </>
                break;
            case (FormFieldValueTypeEnum.DatePicker): {
                const sessionType = getSubsectionFieldValue(data?.formFieldKeys ?? [], field.subSectionId, FormFieldSpecialType.SessionTypeName);
                const sessionPeriod = getSubsectionFieldValue(data?.formFieldKeys ?? [], field.subSectionId, FormFieldSpecialType.SessionPeriod);
                const visitDateReady = field.formFieldKeySpecialTypeId !== FormFieldSpecialType.SessionDateTime
                    || isVisitDateSelectionReady(sessionType, sessionPeriod, schoolTypeValue);
                return <>
                    <BookingDatePicker
                        value={field.formField?.fieldValue ?? ''}
                        label={field.label}
                        required={field.isRequired}
                        onChange={(e: any) => { handleChange(e, index); }}
                        withTime={false}
                        hasParent
                        specialist={
                            data?.formFieldKeys?.find(m => m.shortName === 'specialist')?.formField?.fieldValue
                        }
                        name={field.shortName}
                        schoolType={schoolTypeValue}
                        sessionType={sessionType}
                        sessionPeriod={sessionPeriod}
                        extraDisabledDates={getExtraDisabledDatesForVisit(data?.formFieldKeys ?? [], field, schoolTypeValue)}
                        maxDaysAhead={field.maxDaysAhead ?? null}
                        startTime={field.startTime ?? null}
                        endTime={field.endTime ?? null}
                        timeStepMinutes={15}
                        disabled={isDisabled || !visitDateReady}
                        readOnly={true}
                    />

                </>
                }
                break;
            case (FormFieldValueTypeEnum.Link):
                return <>
                    <Link
                        href={field.label}
                        color="info"
                        underline="always"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {field.label}
                    </Link>
                </>
                break;
            default: return ";";
        }
    }

    function isFormValid() {
        var isFormValid = true;
        getSections().filter(s => checkIfDependancyMet(s)).forEach((s, index) => {
            getSectionKeys(s.id)?.filter((m: any) => checkIfDependancyMet(m)).filter((m: any) => m.isRequired).forEach(ffk => {
                if (ffk.isRequired && (ffk.formField?.fieldValue === null || ffk.formField?.fieldValue === undefined || ffk.formField?.fieldValue === "")) {
                    isFormValid = false;
                }
            });
        });
        return isFormValid;
    }

    function getMultipleFromFieldValue(ffkIds: Array<any>) {
        if (!ffkIds || !ffkIds.length) return undefined;

        let _ffkdIds: Array<ParentItem> = [];

        ffkIds.forEach(m => {
            _ffkdIds.push({ propertyName: m.propertyName, value: getFormFieldValue(m.parentFormFieldKeyId) ?? "" });
        });
        return _ffkdIds;
    }

    function getFormFieldValue(ffkId: any) {
        if (!ffkId) return undefined;

        var globalKey;
        var key = data?.formFieldKeys?.find(m => m.id == ffkId);

        if (!key) {
            globalKey = data?.globalFormFields?.find(m => m.formFieldKeyId == ffkId);
        }

        if (!key && !globalKey) {
            return undefined;
        }

        return key?.formField?.fieldValue || globalKey?.fieldValue;
    }

    function checkIfLookupDependancyMet(ffkfl: IFormFieldKeyLookupManage) {
        var _return = false;
        if (!ffkfl.conditionFormFieldKeyId) return true;
        var globalFormField = data?.globalFormFields?.find(m => m.formFieldKeyId == ffkfl.conditionFormFieldKeyId);
        if (globalFormField && globalFormField.fieldValue && ffkfl.conditionFormFieldKeyValue && globalFormField.fieldValue.toString().toLowerCase() === ffkfl.conditionFormFieldKeyValue.toString().toLowerCase()) {
            _return = true;
        }
        return _return;
    }


    function checkIfDependancyMet(formFieldKey: IFormFieldKeyManage) {
        if ((!formFieldKey.formFieldKeyFormFieldKeyLookups || !formFieldKey.formFieldKeyFormFieldKeyLookups.length) && (!formFieldKey.formFieldKeyConditionDependencies || !formFieldKey.formFieldKeyConditionDependencies.length))
            return true;

        if (formFieldKey.formFieldKeyFormFieldKeyLookups && formFieldKey.formFieldKeyFormFieldKeyLookups.length) {
            var _return: boolean | null = null;

            formFieldKey.formFieldKeyFormFieldKeyLookups.forEach(ffkfl => {
                if (data && data.formFieldKeys && data.formFieldKeys) {
                    data.formFieldKeys.filter(ffk => ffk.formFieldKeyLookups).forEach(ffk => {
                        if (ffk.formFieldKeyLookups?.some(ffkl => ffkl.id == ffkfl.formFieldKeyLookupId)) {
                            if (!!ffk && !!ffk.formField && ffk.formField.fieldValue == ffkfl.value) _return = true;
                        }
                    });

                    var globalFormField = data?.globalFormFields?.find(m => m.formFieldKeyId == ffkfl.formFieldKeyLookupId);
                    if (globalFormField && globalFormField.fieldValue && ffkfl && ffkfl.value && globalFormField.fieldValue.toString().toLowerCase() === ffkfl.value.toString().toLowerCase()) {
                        _return = true;
                    }
                }
            });

            if (_return !== true) {
                _return = false;
                return false;
            }
        }

        if (formFieldKey.formFieldKeyConditionDependencies && formFieldKey.formFieldKeyConditionDependencies.length) {
            var _return: boolean | null = null;
            formFieldKey.formFieldKeyConditionDependencies.forEach((ffkcd: any) => {
                if (_return === true && formFieldKey.formFieldKeyConditionTypeId === FormFieldKeyConditionTypeEnum.Any)
                    return;

                var localField = data?.formFieldKeys?.find(m => m.id == ffkcd.conditionFormFieldKeyId);
                var globalFormField = data?.globalFormFields?.find(m => m.formFieldKeyId == ffkcd.conditionFormFieldKeyId);

                if (!!globalFormField) {
                    _return = checkCondition(_return, formFieldKey, ffkcd, globalFormField);
                }
                else if (!!localField) {
                    _return = checkCondition(_return, formFieldKey, ffkcd, localField.formField);
                }
                else {
                    _return = false;
                    return;
                }

            });

            if (_return !== true) {
                _return = false;
                return false;
            }
        }

        return true;
    }

    function checkCondition(_return: boolean | null, formFieldKey: any, ffkcd: any, field: any) {

        if (!field) return _return;

        if (_return === null ||
            (_return === true && formFieldKey.formFieldKeyConditionTypeId === FormFieldKeyConditionTypeEnum.All) ||
            (_return !== true && formFieldKey.formFieldKeyConditionTypeId === FormFieldKeyConditionTypeEnum.Any)) {
            if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.EqualsTo) {
                _return = (!!field.fieldValue && !!ffkcd.value) ? field.fieldValue.toString().toLowerCase() === ffkcd.value.toString().toLowerCase() : field.fieldValue === ffkcd.value;
            }
            else if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.EqualsOrGreater) {
                _return = Number(field.fieldValue ?? "0") >= Number(ffkcd.value ?? "0");
            }
            else if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.EqualsOrLess) {
                _return = Number(field.fieldValue ?? "0") <= Number(ffkcd.value ?? "0");
            }
            else if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.Less) {
                _return = Number(field.fieldValue ?? "0") < Number(ffkcd.value ?? "0");
            }
            else if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.Greater) {
                _return = Number(field.fieldValue ?? "0") > Number(ffkcd.value ?? "0");
            }
            else if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.Contains) {
                _return = field.fieldValue && ffkcd.value && field.fieldValue.trim().toLowerCase().includes(ffkcd.value.trim().toLowerCase());
            }
            else if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.NotEqualsTo) {
                _return = (!!field.fieldValue && !!ffkcd.value) ? field.fieldValue.toString().toLowerCase() !== ffkcd.value.toString().toLowerCase() : field.fieldValue !== ffkcd.value;
            }
        }

        if (_return === null)
            _return = false;

        return _return;
    }

    function getSections() {
        var subSections: any[] = [];
        if (data && data.formFieldKeys && data.formFieldKeys) {
            subSections = data.formFieldKeys.filter(ffk => ffk.formFieldValueTypeId === FormFieldValueTypeEnum.SubSection);
            if (data.formFieldKeys.some(ffk => ffk.formFieldValueTypeId !== FormFieldValueTypeEnum.SubSection && !ffk.subSectionId))
                subSections.push({});
        }

       return subSections || [{}];
    }

    function getSectionKeys(subSectionId?: any) {
        var keys: any[] = [];
        if (data && data.formFieldKeys && data.formFieldKeys) {

            return data.formFieldKeys.filter(ffk => ffk.formFieldValueTypeId !== FormFieldValueTypeEnum.SubSection && ffk.subSectionId == subSectionId)
        }

        return [];
    }

    function withdraw() {
        if (window.confirm("Are you sure you want to withdraw this form?") == true) {
            setIsSaving(true);
            formService.disable(_formId, FormStatusEnum.Withdrawn).then(
                () => { navigate("/") },
                handleErrorResponse
            ).finally(() => {
                setIsSaving(false)
            });

        }
    }

    return (
        <Grid2 container spacing={1} style={{ padding: "0px" }} id="form-manage" key={"form_template_section_" + params.formTemplateSectionId + "_"}>
            <Grid2 size={{ xs: 12 }}>

                {(isLoading || saving || params.isSectionSaving) &&
                    <Paper>
                        <Typography variant="body1" className="paper-theme-body" gutterBottom>
                            <LinearProgress color="secondary" />
                        </Typography>
                    </Paper>
                }
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enIE}>
                    <ValidatorForm onSubmit={submit} noValidate>

                        {getSections().filter(s => checkIfDependancyMet(s)).map((s, index) =>
                            <>
                                <Paper key={"section_" + s.id + "_" + index + "_"} sx={{ marginBottom: "8px", marginTop: "32px !important", paddingTop: "11px" }}>

                                    <Grid2 container spacing={2}>
                                        {getSectionKeys(s.id).filter(s => checkIfDependancyMet(s)).map((ffk, index) => <>
                                            <Grid2 key={"subsection" + s.id + "_" + index + "_" + ffk.id} size={{ xs: ffk.xsWidth || 12, md: ffk.mdWidth || 12 }}>
                                                {getField(ffk, ffk.id)}
                                            </Grid2>
                                        </>)}
                                    </Grid2>

                                </Paper>
                            </>
                        )}

                        {!params.isForReview &&
                            <Grid2 container spacing={1} style={{ padding: "0px" }}>
                                {!accountService.isAdmin &&
                                    <Grid2 size={{ xs: 4 }} >
                                        <StandardButton onClick={withdraw} saving={isLoading || saving || params.isSectionSaving} icon="remove" type="button">Withdraw</StandardButton>
                                    </Grid2>
                                }
                                <Grid2 size={{ xs: accountService.isAdmin ? 12 : 8 }} >
                                    <StandardButton
                                        onClick={() => { setContinue(true) }}
                                        icon={"check"} saving={isLoading || saving || params.isSectionSaving}
                                        variant="success"
                                        type="submit">
                                        Continue
                                    </StandardButton>
                                </Grid2>
                            </Grid2>
                        }

                    </ValidatorForm >
                </LocalizationProvider>

            </Grid2 >

        </Grid2 >
    );
}